import {
  getCurrentFacilityId,
  useAuth,
} from "@risalabs_frontend_org/auth-wrapper";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LoaderMessage } from "../components/loaderMessage/loaderMessage";
import { isHealthFacilityFeatureEnabled } from "../data-model/healthFacility";
import {
  FacilityCategory,
  isExceptionFlowForRadiologyOrg,
} from "../enums/organizationTypes";
import { loginSuccess, resetAllStates } from "../redux/slice/auth-slice";
import { fetchauthStatusOptions } from "../redux/slice/authStatusOptionsSlice";
import { fetchBOOptions } from "../redux/slice/boOptionsSlice";
import { fetchAllCmmFormConfiguration } from "../redux/slice/cmm/cmmFormConfig";
import { fetchDenialReasons } from "../redux/slice/denialReasonsSlice";
import { fetchEstimateOptions } from "../redux/slice/estimateOptionsSlice";
import { fetchAllFormOptionsFromFirebase } from "../redux/slice/formOptionsSlice";
import { fetchMedicalPaFormConfig } from "../redux/slice/medicalPa/medicalPaFormConfig";
import {
  getProviderDetailsFor,
  resetProviderDetails,
} from "../redux/slice/providerDetailsSlice";
import { fetchQueryReasons } from "../redux/slice/queryReasonsSlice";
import { fetchSingleHealthFacilityDetailsFromFirebase } from "../redux/slice/singleHealthFacilityDetailsSlice";
import {
  fetchAllTestLocationForOrg,
  resetTestLocationDetails,
} from "../redux/slice/testLocationDetailsSlice";
import { AppDispatch, RootState } from "../redux/store/store";
import {
  UserGeneralInfoContextKeys,
  UserStore,
} from "../store/UserGeneralInfoStore";

/**
 * Bridges auth-wrapper authentication state into the medonc dashboard's
 * own Redux store and UserStore, and gates `children` until the critical
 * post-auth slices (providerDetails, singleHealthFacilityDetails, and
 * authStatusOptions) are loaded. Test location catalog loading is modal-
 * scoped and does not block the dashboard.
 *
 * After facility resolution, also dispatches org option fetches in parallel:
 * `fetchBOOptions`, `fetchEstimateOptions`, and `fetchauthStatusOptions`
 * (using the internal org id for external organizations), plus denial and
 * query reasons for the facility's oncology or radiology workflow.
 *
 * If a slice errors or the bootstrap exceeds BOOTSTRAP_TIMEOUT_MS, children render
 * anyway so the user is never trapped.
 */
const BOOTSTRAP_TIMEOUT_MS = 5000;
const ONCOLOGY_REASONS_ORGANIZATION = "oncology";

/**
 * Resolves the reason-configuration path segment for a healthcare facility.
 *
 * @param category - Facility `category` from the loaded healthcare facility doc.
 * @returns `radiology` when the facility is a radiology exception-flow org;
 *   otherwise `oncology`.
 */
const getReasonsOrganization = (category?: string | null): string =>
  isExceptionFlowForRadiologyOrg(category)
    ? FacilityCategory.RADIOLOGY
    : ONCOLOGY_REASONS_ORGANIZATION;

const AuthDataBootstrap = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated, isLoading, user, facility } = useAuth();
  const { data: providerDetails, error: providerError } = useSelector(
    (state: RootState) => state.providerDetails,
  );
  const { data: singleHealthFacilityDetails, error: facilityError } =
    useSelector((state: RootState) => state.singleHealthFacilityDetails);
  const { data: authStatusOptions, error: authStatusOptionsError } =
    useSelector((state: RootState) => state.authStatusOptions);
  const { loading: denialReasonsLoading } = useSelector(
    (state: RootState) => state.denialReasons,
  );
  const { loading: queryReasonsLoading } = useSelector(
    (state: RootState) => state.queryReasons,
  );
  const isProviderForCurrentUser =
    Boolean(user?.id) && providerDetails?.id === user?.id;
  const providerFacilityId = isProviderForCurrentUser
    ? providerDetails?.FacilityId
    : "";
  const hasFetchedGlobalConfigRef = useRef(false);
  const lastFacilityBootstrappedRef = useRef<string>("");
  const lastOptionsFacilityRef = useRef<string>("");
  const lastReasonsOrganizationRef = useRef<string>("");

  const [bootstrapTimedOut, setBootstrapTimedOut] = useState(false);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!isAuthenticated || !user?.id) {
      hasFetchedGlobalConfigRef.current = false;
      lastFacilityBootstrappedRef.current = "";
      lastOptionsFacilityRef.current = "";
      lastReasonsOrganizationRef.current = "";
      dispatch(resetAllStates());
      dispatch(resetProviderDetails());
      dispatch(resetTestLocationDetails());
      return;
    }

    dispatch(
      loginSuccess({
        email: user.email,
        id: user.id,
      }),
    );
    UserStore.setItem(UserGeneralInfoContextKeys.USER_ID, user.id);

    if (!hasFetchedGlobalConfigRef.current && isProviderForCurrentUser) {
      hasFetchedGlobalConfigRef.current = true;
      dispatch(fetchAllFormOptionsFromFirebase());
      dispatch(fetchAllCmmFormConfiguration());
      dispatch(fetchMedicalPaFormConfig());
    }

    if (providerDetails?.id !== user.id) {
      dispatch(getProviderDetailsFor(user.id));
    }
  }, [
    dispatch,
    isAuthenticated,
    isLoading,
    isProviderForCurrentUser,
    providerDetails?.id,
    user,
  ]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const resolvedFacilityId =
      facility?.id || providerFacilityId || getCurrentFacilityId() || "";

    if (
      !resolvedFacilityId ||
      lastFacilityBootstrappedRef.current === resolvedFacilityId
    ) {
      return;
    }

    lastFacilityBootstrappedRef.current = resolvedFacilityId;
    UserStore.setItem(
      UserGeneralInfoContextKeys.HEALTHCARE_FACILITY_ID,
      resolvedFacilityId,
    );
    dispatch(fetchSingleHealthFacilityDetailsFromFirebase(resolvedFacilityId));
    // Drop prior facility's locations, then load for the resolved org.
    dispatch(resetTestLocationDetails());
    dispatch(fetchAllTestLocationForOrg());
  }, [dispatch, facility?.id, isAuthenticated, providerFacilityId]);

  useEffect(() => {
    if (!isAuthenticated || !singleHealthFacilityDetails) {
      return;
    }

    UserStore.setItem(
      UserGeneralInfoContextKeys.ENABLE_REVIEW_MODE,
      isHealthFacilityFeatureEnabled(
        singleHealthFacilityDetails,
        "enable_review_mode",
      ),
    );

    const reasonsOrganization = getReasonsOrganization(
      singleHealthFacilityDetails.category,
    );
    if (lastReasonsOrganizationRef.current !== reasonsOrganization) {
      lastReasonsOrganizationRef.current = reasonsOrganization;
      dispatch(fetchDenialReasons(reasonsOrganization));
      dispatch(fetchQueryReasons(reasonsOrganization));
    }

    let optionsFacilityId = getCurrentFacilityId() || providerFacilityId || "";

    if (
      singleHealthFacilityDetails.is_external_organization &&
      singleHealthFacilityDetails.corresponding_internal_org_id
    ) {
      UserStore.setItem(
        UserGeneralInfoContextKeys.INTERNAL_ORGANIZATION_FACILITY_ID,
        singleHealthFacilityDetails.corresponding_internal_org_id,
      );
      optionsFacilityId =
        singleHealthFacilityDetails.corresponding_internal_org_id;
    }

    if (
      !optionsFacilityId ||
      lastOptionsFacilityRef.current === optionsFacilityId
    ) {
      return;
    }

    lastOptionsFacilityRef.current = optionsFacilityId;
    dispatch(fetchBOOptions(optionsFacilityId));
    dispatch(fetchEstimateOptions(optionsFacilityId));
    dispatch(fetchauthStatusOptions(optionsFacilityId));
  }, [
    dispatch,
    isAuthenticated,
    providerFacilityId,
    singleHealthFacilityDetails,
  ]);

  useEffect(() => {
    if (!isAuthenticated) {
      setBootstrapTimedOut(false);
      return;
    }
    const id = window.setTimeout(
      () => setBootstrapTimedOut(true),
      BOOTSTRAP_TIMEOUT_MS,
    );
    return () => window.clearTimeout(id);
  }, [isAuthenticated]);

  const criticalDataLoaded =
    Boolean(providerDetails) &&
    Boolean(singleHealthFacilityDetails) &&
    authStatusOptions.length > 0;
  const criticalDataErrored =
    Boolean(providerError) ||
    Boolean(facilityError) ||
    Boolean(authStatusOptionsError);
  const areReasonsLoading = denialReasonsLoading || queryReasonsLoading;
  const showLoadingScreen =
    isAuthenticated &&
    (!criticalDataLoaded || areReasonsLoading) &&
    !criticalDataErrored &&
    !bootstrapTimedOut;

  if (showLoadingScreen) {
    return (
      <div style={{ height: "100vh", width: "100vw" }}>
        <LoaderMessage message="Loading your dashboard..." />
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthDataBootstrap;
